import React, { useState, useEffect } from 'react';
import {
    getListAnnouncementByBuildingId,
    addAnnouncement,
    updateAnnouncement,
    deleteAnnouncement
} from '../../Fetch/fetchAnnouncement';
import styles from './Announcement.module.css';

const Announcement = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [buildingId, setBuildingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: ''
    });

    // Fetch user data and announcements on component mount
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            const userId = user.userId;
            const userBuildingId = user.buildingId;

            setCurrentUserId(userId);
            setBuildingId(userBuildingId);

            // Fetch announcements after setting the building ID
            fetchAnnouncementsForBuilding(userBuildingId);
        } else {
            setLoading(false);
            setError('User data not found. Please log in again.');
        }
    }, []);

    const fetchAnnouncementsForBuilding = async (buildingIdToFetch) => {
        try {
            setLoading(true);
            const data = await getListAnnouncementByBuildingId(buildingIdToFetch);
            
            // Debug: Log the response to check structure
            
            setAnnouncements(data);
            setError(null);
        } catch (err) {
            setError('Failed to load announcements');
        } finally {
            setLoading(false);
        }
    };

    const fetchAnnouncements = async () => {
        if (buildingId) {
            await fetchAnnouncementsForBuilding(buildingId);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingAnnouncement) {
                // Update existing announcement - use UpdateAnnouncementDTO structure
                const announcementId = editingAnnouncement.AnnouncementID ?? editingAnnouncement.announcementID;
                
                if (!announcementId) {
                    setError('Unable to update: Announcement ID not found');
                    return;
                }

                const updateData = {
                    AnnouncementID: announcementId,
                    PostedByUserID: parseInt(currentUserId),
                    Title: formData.title,
                    Content: formData.content
                };
                await updateAnnouncement(updateData);
            } else {
                // Add new announcement - use CreateAnnouncementDTO structure
                const newData = {
                    BuildingID: parseInt(buildingId),
                    PostedByUserID: parseInt(currentUserId),
                    Title: formData.title,
                    Content: formData.content
                };
                await addAnnouncement(newData);
            }

            // Reset form and refresh announcements
            setFormData({
                title: '',
                content: ''
            });
            setShowAddForm(false);
            setEditingAnnouncement(null);
            await fetchAnnouncements();

        } catch (err) {
            setError(err.message || `Failed to ${editingAnnouncement ? 'update' : 'add'} announcement`);
        }
    };

    const handleEdit = (announcement) => {
        setEditingAnnouncement(announcement);
        setFormData({
            title: announcement.Title || announcement.title || '',
            content: announcement.Content || announcement.content || ''
        });
        setShowAddForm(true);
    };

    const handleDelete = async (announcement) => {
        const announcementId = announcement.AnnouncementID ?? announcement.announcementID;

        if (!announcementId) {
            setError('Unable to delete: Announcement ID not found');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this announcement?')) {
            return;
        }

        try {
            await deleteAnnouncement(announcementId, currentUserId);
            await fetchAnnouncements();
        } catch (err) {
            setError(err.message || 'Failed to delete announcement');
        }
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setEditingAnnouncement(null);
        setFormData({
            title: '',
            content: ''
        });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return <div className={styles.loading}>Loading announcements...</div>;
    }

    return (
        <div className={styles.announcementContainer}>
            <div className={styles.header}>
                <h2 className={styles.title}>הודעות</h2>
                <button
                    className={styles.addButton}
                    onClick={() => setShowAddForm(!showAddForm)}
                >
                    {showAddForm ? 'ביטול' : 'הוסף הודעה'}
                </button>
            </div>

            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            {showAddForm && (
                <div className={styles.formContainer}>
                    <h3 className={styles.formTitle}>
                        {editingAnnouncement ? 'עריכה' : 'הוסף הודעה'}
                    </h3>
                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label htmlFor="title" className={styles.label}>Title:</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                className={styles.input}
                                placeholder="Enter announcement title"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="content" className={styles.label}>Content:</label>
                            <textarea
                                id="content"
                                name="content"
                                value={formData.content}
                                onChange={handleInputChange}
                                required
                                rows="4"
                                className={styles.textarea}
                                placeholder="Enter announcement content"
                            />
                        </div>

                        <div className={styles.formActions}>
                            <button type="submit" className={styles.submitButton}>
                                {editingAnnouncement ? 'עדכון' : 'הוסף'} Announcement
                            </button>
                            <button type="button" onClick={handleCancel} className={styles.cancelButton}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className={styles.announcementsList}>
                {announcements.length === 0 ? (
                    <div className={styles.noAnnouncements}>
                        No announcements found for this building.
                    </div>
                ) : (
                    announcements.map((announcement, index) => {
                        // Ensure we have a unique key - check for both possible property names and handle 0 values
                        const announcementId = announcement.AnnouncementID ?? announcement.announcementID;
                        const uniqueKey = (announcementId !== undefined && announcementId !== null) 
                            ? `announcement-${announcementId}` 
                            : `announcement-index-${index}`;
                        
                        return (
                            <div key={uniqueKey} className={styles.announcementCard}>
                                <div className={styles.announcementHeader}>
                                    <h3 className={styles.announcementTitle}>
                                        {announcement.Title || announcement.title || 'Untitled'}
                                    </h3>
                                    <div className={styles.announcementActions}>
                                        {(announcement.PostedByUserID ?? announcement.postedByUserID) === currentUserId && (
                                            <>
                                                <button
                                                    onClick={() => handleEdit(announcement)}
                                                    className={styles.editButton}
                                                >
                                                    עריכה
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(announcement)}
                                                    className={styles.deleteButton}
                                                >
                                                    מחיקה
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.announcementContent}>
                                    {announcement.Content || announcement.content || 'No content'}
                                </div>

                                <div className={styles.announcementFooter}>
                                    <span className={styles.announcementDate}>
                                        Posted: {formatDate(announcement.DatePosted || announcement.datePosted)}
                                    </span>
                                    <span className={styles.announcementAuthor}>
                                        By: {announcement.PostedByUserName || announcement.postedByUserName || `User ${announcement.PostedByUserID || announcement.postedByUserID}`}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default Announcement;